
<template>
  <div class="container-fluid">
    <Toolbar class="m-2">
      <template #start>
        <h2>{{ $t('nav.equipmentType') }}</h2>
      </template>
      <template #end>
        <Button
          :label="$t('equipmentTypePage.createEquipmentType')"
          icon="pi pi-plus"
          class=" mr-2 p-button-outlined p-button-rounded"
          @click="toEquipmentTypeForm()"
        />
      </template>
    </Toolbar>

    <DataTable
      v-model:sortField="equipmentTypeFilter.pageable.sortField"
      v-model:sortOrder="equipmentTypeFilter.pageable.sortOrder"
      v-model:filters="equipmentTypeFilter.filters"
      v-model:rows="equipmentTypeFilter.pageable.size"
      striped-rows
      row-hover
      lazy
      paginator
      paginator-position="both"
      :value="equipmentTypes"
      :loading="isLoading"
      :rows="equipmentTypeFilter.pageable.size"
      :total-records="totalEquipmentTypesCount"
      @page="onPageChange($event)"
      @sort="onSort($event)"
      @filter="getEquipmentTypes"
      responsive-layout="scroll"
      class="p-datatable-sm"
      filter-display="row"
    >
      <Column
        :exportable="false"
        style="width: 175px; min-width: 175px;"
      >
        <template #body="{data}">
          <Button
            icon="pi pi-pencil"
            class="p-button-rounded mx-1"
            :title="$t('action.crud.update')"
            @click="toEquipmentTypeForm(data.id)"
          />

          <Button
            icon="pi pi-trash"
            class="p-button-rounded p-button-danger mx-1"
            :title="$t('action.crud.delete')"
            @click="openDeleteDialog(data.id)"
          />
        </template>
      </Column>
      <Column
        field="code"
        :header="$t('equipmentTypePage.code')"
        :sortable="true"
        :show-filter-menu="false"
        class="col-tab col-4"
      >
        <template #body="{ data }">
          {{ data.code }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
      </Column>
      <Column class="col-tab col-4" field="frenchWording" :header="$t('equipmentTypePage.frenchWording')"></Column>
      <Column class="col-tab col-4" field="englishWording" :header="$t('equipmentTypePage.englishWording')"></Column>
    </DataTable>

    <Dialog
      v-model:visible="dialogDelete"
      :modal="true"
    >
      <template #header>
        <h3>{{ $t('equipmentTypePage.dialog.delete.header') }}</h3>
      </template>

      <div class="confirmation-content">
        <span>{{ $t("equipmentTypePage.dialog.delete.content") }}</span>
      </div>
      <template #footer>
        <Button
          :label="$t('equipmentTypePage.dialog.delete.abortButton')"
          icon="pi pi-times"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="dialogDelete = false"
        />
        <Button
          :label="$t('equipmentTypePage.dialog.delete.confirmButton')"
          icon="pi pi-check"
          class="p-button-success mr-2 p-button-outlined p-button-rounded"
          @click="deleteEquipmentType"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import DataTable from "primevue/datatable";
import Button from "primevue/button";
import Toolbar from "primevue/toolbar";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import Dialog from "primevue/dialog";
import {reactive} from "vue";
import useEquipmentTypeFilter from "@/composables/filters/useEquipmentTypeFilter";

export default {
  name: "EquipmentTypeTable",
  components: { DataTable, Button, Toolbar, Column, InputText, Dialog },
  setup(){
    const equipmentTypeFilter = reactive(useEquipmentTypeFilter());

    return {
      equipmentTypeFilter: equipmentTypeFilter,
    }
  },
  data() {
    return {
      /** @type {EquipmentTypeDto[]}*/
      equipmentTypes: [],
      totalEquipmentTypesCount: 0,
      isLoading: false,
      selectedId: null,
      dialogDelete: false,
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    async init() {
      await this.getEquipmentTypes();
    },
    async getEquipmentTypes() {
      this.isLoading = true;
      try {
        const { content, totalElements } = await this.$equipmentTypeService.fetchEquipmentTypesPagingAndSorting(this.equipmentTypeFilter.getQueryParams());
        this.equipmentTypes = content;
        this.totalEquipmentTypesCount = totalElements;
      } catch (e) {
        console.error(e);
        this.$emitter.emit('toast', { severity: 'error', message: 'error.global' });
      } finally {
        this.isLoading = false;
      }
    },
    onPageChange(event) {
      this.equipmentTypeFilter.pageable.page = event.page;
      this.getEquipmentTypes();
    },
    onSort(event) {
      this.equipmentTypeFilter.pageable.sortField = event.sortField;
      this.equipmentTypeFilter.pageable.sortOrder = event.sortOrder;
      this.getEquipmentTypes();
    },
    toEquipmentTypeForm(equipmentTypeId) {
      if (equipmentTypeId) {
        this.$router.push({name: "equipmentTypeUpdate", params: {id: equipmentTypeId}});
      } else {
        this.$router.push({name: "equipmentTypeCreate"});
      }
    },
    openDeleteDialog(id) {
      this.selectedId = id;
      this.dialogDelete = true;
    },
    async deleteEquipmentType() {
      this.dialogDelete = false;
      try {
        const del = await this.$equipmentTypeService.deleteEquipmentType(this.selectedId);
        if (del) {
          this.$emitter.emit("toast", {
            severity: "success",
            message: "equipmentTypePage.toast.delete.success",
          });
        } else {
          this.$emitter.emit("toast", {
            severity: "error",
            message: "equipmentTypePage.toast.delete.error",
          });
        }
      } catch (e) {
        const errors = e.response.data.map(error => this.$t(error)).join(', ')
        this.$emitter.emit("toast", {
          severity: "error",
          message: errors,
        });
      }
      this.getEquipmentTypes();
    },
  }
}
</script>

<style scoped lang="scss">
.pi-check {
  color: white;
}
</style>
